import { createSelector } from 'reselect'
import { isArray, groupBy, compact, find, last, isEmpty } from 'lodash'

import { nonZeroDisplayAmount } from '../utils/orderUtils'
import { TabOrderDetailsViewModel } from '../viewmodels/TabOrderViewModel'
import { makeGetDisplaySuiteByOrder } from './suites'
import { makeGetOrder, makeGetServiceChargeLegalText } from './order'
import { centsToDollars, stringFloatToCents } from '../utils/formatters'
import PaymentTypes from '../utils/paymentTypes'

const capitalizeWords = (str) => {
  if (!str) return str

  // If there are double spaces or tab characters in the string, this function breaks
  const sanitzedString = str.trim().replace(/(\s|\t){1,}/g, ' ')
  const words = sanitzedString.split(' ')
  const result = words.reduce((accumulator, word) => {
    return `${accumulator}${!accumulator ? '' : ' '}${word[0].toUpperCase() + word.slice(1)}`
  }, '')

  return result
}

const formatCardReaderDisplay = (order, userAttributes) => {
  let lastFour = userAttributes?.last4
  let cardIssuer = userAttributes?.freedompayIssuerName?.trim?.()

  return `${cardIssuer} ${lastFour}`
}

export const makeGetDigitalReceipt = () => createSelector(
  makeGetOrder(), makeGetDisplaySuiteByOrder(), makeGetServiceChargeLegalText(), (state, orderId, paymentId) => ({ state, paymentId }),
  (order, suiteInformation, disclaimer, args) => {
    const { state, paymentId } = args

    const orderViewModel = TabOrderDetailsViewModel(order, { sumGroupedItems: true, excludeFreeModifiers: true, includeModifierPrices: true })
    const receipt = state?.receipt ?? {}
    const tenderPayment = find(order?.paymentInfo, (payment) => payment?.uuid === paymentId) ?? last(order?.paymentInfo) ?? {}
    const tenderDisplayAmount = paymentId ? centsToDollars(tenderPayment?.amountInCents - tenderPayment?.tip) : receipt?.total
    const gratuityDisplayAmount = paymentId ? centsToDollars(tenderPayment?.tip) : receipt?.gratuity
    const total = paymentId ? centsToDollars(tenderPayment?.amountInCents) : centsToDollars(stringFloatToCents(receipt?.total) + stringFloatToCents(receipt?.gratuity))

    let paymentDescription
    if (!paymentId) {
      paymentDescription = orderViewModel?.payment ?? ''
    } else
    if (PaymentTypes.isCardReader(tenderPayment?.paymentType)) {
      paymentDescription = formatCardReaderDisplay(order, tenderPayment?.userAttributes)
    } else {
      paymentDescription = tenderPayment?.shortDescription ?? tenderPayment?.paymentType
    }

    let taxRules = []
    if (isArray(receipt?.taxes)) {
      taxRules = receipt?.taxes?.map((item) => {
        return {
          ...item,
          price: item?.amount,
        }
      })
    }

    const subtotal = {
      name: 'Subtotal',
      price: receipt?.subtotal
    }

    const discount = {
      name: 'Discount',
      price: receipt?.discount,
    }

    const serviceCharge = {
      name: receipt?.serviceChargeName ?? 'Service Charge',
      price: receipt?.serviceCharge,
    }

    const gratuity = {
      name: 'Gratuity',
      price: gratuityDisplayAmount,
    }

    const orderTotal = {
      name: 'Total',
      price: receipt?.total,
    }

    const tenderAmount = {
      name: 'Tender Amount',
      price: tenderDisplayAmount
    }

    const secondaryItems = compact([
      subtotal,
      nonZeroDisplayAmount(receipt?.discount) ? discount : undefined,
      nonZeroDisplayAmount(receipt?.serviceCharge) ? serviceCharge : undefined,
      ...taxRules,
      orderTotal,
    ])

    const tertiaryItems = compact([
      tenderAmount,
      nonZeroDisplayAmount(gratuityDisplayAmount) ? gratuity : undefined,
    ])

    const customer = suiteInformation.displayName

    const result = {
      total,
      header: receipt?.standName ?? '',
      customer,
      attendant: receipt?.attendantName && `Attendant: ${receipt?.attendantName}`,
      customerOrderString: `${customer} Order #${order.orderNumber}`,
      payment: capitalizeWords(paymentDescription),
      groupedItems: groupBy(receipt?.lineItems, 'menuType'),
      secondaryItems,
      tertiaryItems,
      disclaimer,
      loading: receipt?.loading ?? false,
      failed: receipt?.failed ?? false
    }

    return result
  }
)
